import { Box, fade, makeStyles, Paper, Typography, useTheme } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React, { useCallback } from 'react'
import { useKpiItemStyles } from './kpiItem.styles'

export interface KpiItemProps {
  value: number | string | undefined
  description: string
  icon: IconProp
  isLoading?: boolean
  onClick?: () => void
  /** Estado activo - muestra la card con relleno de color primario */
  isActive?: boolean
  /** Color del icono/badge en estado inactivo. Por defecto usa el primary del theme */
  color?: string
  /** 'hero' - variante apilada/centrada para destacar un único KPI (ej. leftContent) */
  size?: 'normal' | 'hero'
}

const useStyles = makeStyles(useKpiItemStyles)

const KpiItem: React.FC<KpiItemProps> = ({
  value,
  description,
  icon,
  isLoading = false,
  onClick,
  isActive = false,
  color,
  size = 'normal'
}) => {
  const theme = useTheme()
  const classes = useStyles(theme)
  const badgeColor = color ?? theme.palette.primary.main
  const isHero = size === 'hero'

  const handleClick = useCallback(() => {
    onClick?.()
  }, [onClick])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (onClick && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault()
        onClick()
      }
    },
    [onClick]
  )

  return (
    <Paper
      elevation={0}
      className={clsx(
        classes.root,
        isHero && classes.rootHero,
        onClick && classes.clickable,
        isActive && classes.active
      )}
      onClick={onClick && handleClick}
      onKeyDown={onClick && handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-pressed={onClick ? isActive : undefined}
    >
      <Box
        className={clsx(classes.iconBadge, isHero && classes.iconBadgeHero)}
        style={{ backgroundColor: fade(badgeColor, 0.12), color: badgeColor }}
      >
        <FontAwesomeIcon icon={icon} />
      </Box>
      <Box className={classes.content}>
        {isLoading ? (
          <Skeleton variant="rect" width={60} height={38} className={classes.skeleton} />
        ) : (
          <Typography className={clsx(classes.valueText, isHero && classes.valueTextHero)}>
            {value ?? 0}
          </Typography>
        )}
        <Typography
          className={clsx(classes.descriptionText, isHero && classes.descriptionTextHero)}
        >
          {description}
        </Typography>
      </Box>
    </Paper>
  )
}

export default KpiItem
